import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'

import { mediaQueryDown } from 'components/utils/breakpoint'

import Switch from 'LuxKit/Switch/Switch'
import BodyText from 'LuxKit/Typography/BodyText'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import LoyaltyBurnInfoModal from 'loyalty/components/LoyaltyBurnInfoModal'
import { MINIMUM_POINTS_TO_APPLY } from 'loyalty/constants/loyaltyBurn'
import noop from 'lib/function/noop'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { SET_SEARCH_BURN_ENABLED } from 'actions/actionConstants'
import useLoyaltyBurnExperiment from 'loyalty/hooks/useLoyaltyBurnExperiment'

const ToggleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(8)} ${rem(16)} ${rem(16)};

  ${mediaQueryDown.mobile} {
    padding-left: 0;
  }

  &.compact {
    padding: 0;
  }
`

const ToggleLeft = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${rem(12)};
  cursor: pointer;
`

const LabelGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${rem(2)};
`

const InfoIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${props => props.theme.palette.neutral.default.three};
`

interface Props {
  compact?: boolean
}

function GlobalSearchLoyaltyBurnToggle({ compact = false }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [tier] = useCurrentLoyaltyTier()
  const showModal = useContext(ModalContext)
  const checked = useAppSelector(state => state.loyalty.hasSelectedBurnToggle)
  const { isAnyVariant: isBurnEnabled } = useLoyaltyBurnExperiment()

  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const userPoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)

  const shouldShow = useMemo(() => {
    if (!isBurnEnabled) return false
    if (!isLoyaltyEnabled) return false
    if (!isLoggedIn) return true
    if (!hasLoyaltyAccount) return false
    return userPoints >= MINIMUM_POINTS_TO_APPLY
  }, [isBurnEnabled, isLoyaltyEnabled, isLoggedIn, hasLoyaltyAccount, userPoints])

  const handleSwitchClick = useCallback(async(e: React.MouseEvent<HTMLInputElement>) => {
    if (!isLoggedIn) {
      e.preventDefault()
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (!result.loggedIn) return
    }
    dispatch({ type: SET_SEARCH_BURN_ENABLED, enabled: !checked })
  }, [isLoggedIn, checked, showModal, dispatch])

  const handleInfoClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    showModal(<LoyaltyBurnInfoModal />)
  }, [showModal])

  if (!shouldShow) {
    return null
  }

  return (
    <ToggleRow className={compact ? 'compact' : undefined}>
      <ToggleLeft>
        <LoyaltyTierIcon tier={tier} size={24} />
        <LabelGroup>
          <BodyText variant="medium" as="span" weight="bold">
            {intl.formatMessage(
              {
                id: 'CmjorX7E1isXMXSQjaRk',
                defaultMessage: 'Use {programName} Points',
                description: 'Label for the loyalty program points toggle in the global search header',
              },
              { programName: getLoyaltyProgramDisplayName() },
            )}
          </BodyText>
          <InfoIconButton
            onClick={handleInfoClick}
            aria-label={intl.formatMessage({
              id: 'wIPquwfzmtfChYHOaGDJ',
              defaultMessage: 'Learn more about {programName} points',
              description: 'Accessibility label for loyalty program points info button on search bar',
            }, { programName: getLoyaltyProgramDisplayName() })}
          >
            <LineInfoCircleIcon size="XS" />
          </InfoIconButton>
        </LabelGroup>
      </ToggleLeft>
      <Switch
        checked={checked}
        onChange={noop}
        onClick={handleSwitchClick}
        aria-label={intl.formatMessage({
          id: 'S_CqN3-bN1w2OccOM1UQ',
          defaultMessage: 'Toggle {programName} points pricing',
          description: 'Accessibility label for the loyalty program points pricing toggle in global search bar',
        }, { programName: getLoyaltyProgramDisplayName() })}
      />
    </ToggleRow>
  )
}

export default GlobalSearchLoyaltyBurnToggle
